import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { sellersApi } from '../../services/apiService';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import SupplierManageModal from '../../components/SupplierManageModal/SupplierManageModal';
import SupplierEditModal from '../../components/SupplierEditModal/SupplierEditModal';
import './AdminSupplierDetailPage.css';
import { useCurrency } from '../../context/CurrencyContext';
import { LOGO_PLACEHOLDER, PRODUCT_PLACEHOLDER } from '../../utils/placeholders';

const AdminSupplierDetailPage = () => {
  const { id } = useParams();
  const { formatPriceRange } = useCurrency();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('home');
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Inquiry form state
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [inquiryLoading, setInquiryLoading] = useState(false);

  // Handle inquiry form input changes
  const handleInquiryChange = (e) => {
    const { name, value } = e.target;
    setInquiryForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Submit inquiry form
  const handleInquirySubmit = async () => {
    // Validate required fields
    if (!inquiryForm.name || !inquiryForm.email || !inquiryForm.subject || !inquiryForm.message) {
      alert('Please fill in all required fields (Name, Email, Subject, Message)');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inquiryForm.email)) {
      alert('Please enter a valid email address');
      return;
    }

    setInquiryLoading(true);
    try {
      const response = await fetch(`${'/api'}/contact/supplier-inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...inquiryForm,
          supplierId: supplier.id,
          supplierName: supplier.name,
          supplierEmail: supplier.email
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('Inquiry sent successfully to ' + supplier.name + '!');
        // Reset form
        setInquiryForm({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: ''
        });
      } else {
        alert(data.error || 'Failed to send inquiry. Please try again.');
      }
    } catch (err) {
      console.error('Error sending inquiry:', err);
      alert('Failed to send inquiry. Please try again.');
    } finally {
      setInquiryLoading(false);
    }
  };

  useEffect(() => {
    const fetchSupplier = async () => {
      try {
        setLoading(true);
        const data = await sellersApi.getByIdAdmin(id);
        console.log('Supplier data:', data);
        setSupplier(data);
      } catch (err) {
        console.error('Error fetching supplier:', err);
        setError('Failed to load supplier details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchSupplier();
    }
  }, [id]);

  const handleStatusChange = async (newStatus) => {
    try {
      await sellersApi.updateStatus(supplier.id, newStatus);
      setSupplier(prev => ({ ...prev, status: newStatus }));
      alert(`Supplier ${newStatus === 'approved' ? 'approved' : 'status updated to ' + newStatus} successfully!`);
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update supplier status');
    }
  };

  const handleSuspend = async () => {
    const reason = window.prompt('Enter reason for suspension (optional):');
    if (reason === null) return; // cancelled
    try {
      await sellersApi.suspend(supplier.id, reason);
      setSupplier(prev => ({ ...prev, status: 'suspended' }));
      alert('Supplier has been suspended.');
    } catch (err) {
      console.error('Error suspending supplier:', err);
      alert('Failed to suspend supplier');
    }
  };

  const handleUnsuspend = async () => {
    if (!window.confirm('Are you sure you want to unsuspend this supplier? Their access will be fully restored.')) return;
    try {
      await sellersApi.unsuspend(supplier.id);
      const data = await sellersApi.getByIdAdmin(id);
      setSupplier(data);
      alert('Supplier has been unsuspended.');
    } catch (err) {
      console.error('Error unsuspending supplier:', err);
      alert('Failed to unsuspend supplier');
    }
  };

  // Handle edit update - refresh supplier data
  const handleEditUpdate = async () => {
    try {
      const data = await sellersApi.getByIdAdmin(id);
      setSupplier(data);
    } catch (err) {
      console.error('Error refreshing supplier:', err);
    }
  };

  // Parse JSON fields safely
  const parseJSON = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    try {
      return JSON.parse(field);
    } catch {
      return [];
    }
  };

  // Handle brochure download
  const handleDownloadBrochure = () => {
    const brochures = parseJSON(supplier.brochures);
    if (brochures.length > 0) {
      const brochureUrl = typeof brochures[0] === 'string' ? brochures[0] : brochures[0]?.url;
      if (brochureUrl) {
        // Open in new tab to download
        window.open(brochureUrl, '_blank');
      } else {
        alert('No brochure file available');
      }
    } else {
      alert('No brochure uploaded for this supplier');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="asd-loading">
          <div className="asd-loading-spinner"></div>
          <p>Loading supplier details...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error || !supplier) {
    return (
      <AdminLayout>
        <div className="asd-error">
          <p>{error || 'Supplier not found'}</p>
          <button onClick={() => navigate('/admin/suppliers')}>Back to Suppliers</button>
        </div>
      </AdminLayout>
    );
  }

  const snapshots = parseJSON(supplier.snapshots);
  const products = supplier.products || [];

  return (
    <AdminLayout>
      <div className="admin-supplier-detail-content">
        {/* Header */}
        <div className="asd-header">
          <button className="asd-back-btn" onClick={() => navigate('/admin/suppliers')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Suppliers
          </button>
          <div className="asd-header-title">
            <h1>Supplier Manage</h1>
            <p>Effortlessly oversee and update your supplier profile, products, and verifications in one place.</p>
          </div>
        </div>

        {/* Cover Image */}
        <div className="asd-cover-section">
          <img 
            src={supplier.coverImage || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200'} 
            alt="Cover" 
            className="asd-cover-image" 
          />
        </div>

        {/* Company Info Section */}
        <div className="asd-company-section">
          <div className="asd-company-header">
            <div className="asd-company-info">
              <img 
                src={supplier.logo || LOGO_PLACEHOLDER} 
                alt={supplier.name}
                className="asd-company-logo"
              />
              <div className="asd-company-details">
                <h2 className="asd-company-name">{supplier.name}</h2>
                <div className="asd-company-badges">
                  {supplier.isGlobal && (
                    <span className="asd-badge global">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      Global
                    </span>
                  )}
                  {supplier.isVerified && (
                    <span className="asd-badge verified">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="22 4 12 14.01 9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Verified
                    </span>
                  )}
                  <span className="asd-badge location">
                    {supplier.countryFlag && <img src={supplier.countryFlag} alt={supplier.country} className="asd-flag" />}
                    {supplier.country} / {supplier.memberYears || 1} Year
                  </span>
                  <span className="asd-badge business-type">
                    {supplier.businessType || 'Manufacturer'}
                  </span>
                </div>
              </div>
            </div>
            <div className="asd-stats-section">
              <span className="asd-stat">{supplier.reviews || 0} Reviews</span>
              <div className="asd-stat-divider"></div>
              <span className="asd-stat">{products.length} Products</span>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="asd-nav-tabs">
            <button 
              className={`asd-nav-tab ${activeTab === 'home' ? 'active' : ''}`}
              onClick={() => setActiveTab('home')}
            >
              Home
            </button>
            <button 
              className={`asd-nav-tab ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              Company Profile
            </button>
            <button 
              className={`asd-nav-tab ${activeTab === 'product' ? 'active' : ''}`}
              onClick={() => setActiveTab('product')}
            >
              Product
            </button>
            <button 
              className={`asd-nav-tab ${activeTab === 'contact' ? 'active' : ''}`}
              onClick={() => setActiveTab('contact')}
            >
              Contact
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'home' && (
          <div className="asd-tab-content">
            {/* Company Overview */}
            <div className="asd-content-row">
              <div className="asd-video-placeholder">
                {supplier.videoUrl ? (
                  supplier.videoUrl.includes('youtube.com') || supplier.videoUrl.includes('youtu.be') ? (
                    <iframe 
                      src={supplier.videoUrl.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')} 
                      title="Company Video" 
                      allowFullScreen
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    ></iframe>
                  ) : supplier.videoUrl.includes('vimeo.com') ? (
                    <iframe 
                      src={supplier.videoUrl.replace('vimeo.com/', 'player.vimeo.com/video/')} 
                      title="Company Video" 
                      allowFullScreen
                    ></iframe>
                  ) : (
                    <video controls className="asd-video-player">
                      <source src={supplier.videoUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  )
                ) : supplier.companyVideo ? (
                  <video controls className="asd-video-player">
                    <source src={supplier.companyVideo} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="asd-video-icon">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                      <circle cx="24" cy="24" r="24" fill="#0397DD" fillOpacity="0.1"/>
                      <path d="M32 24L20 32V16L32 24Z" fill="#0397DD"/>
                    </svg>
                    <p className="asd-no-video-text">No video available</p>
                  </div>
                )}
              </div>
              <div className="asd-content-details">
                <h3>{supplier.name}</h3>
                <p className="asd-tagline">Trusted {supplier.businessType || 'Supplier'} with {supplier.memberYears || 1}+ Years of Industry Experience</p>
                <p className="asd-description">{supplier.description || supplier.companyOverview || 'No description available.'}</p>
                
                <div className="asd-contact-cta">
                  <h4>Contact Us Now</h4>
                  <p>Have questions or ready to get started? Our team is here to help with quotes, product info, or partnership opportunities.</p>
                  <div className="asd-action-buttons">
                    <button 
                      className="asd-btn-primary"
                      onClick={() => setActiveTab('contact')}
                    >
                      Inquire Now
                    </button>
                    <button 
                      className="asd-btn-secondary"
                      onClick={() => {
                        if (supplier.phoneNumber || supplier.phone) {
                          alert(`Mobile: ${supplier.phoneNumber || supplier.phone}`);
                        } else {
                          alert('No phone number available');
                        }
                      }}
                    >
                      View Mobile
                    </button>
                    <button 
                      className="asd-btn-secondary"
                      onClick={() => navigate(`/admin/messages?seller=${supplier.id}&userId=${supplier.userId || ''}`)}
                    >
                      Chat Now
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Highlight */}
            {products.length > 0 && (
              <div className="asd-section">
                <h3>Product Highlight</h3>
                <div className="asd-products-grid">
                  {products.slice(0, 5).map((product) => (
                    <div 
                      key={product.id} 
                      className="asd-product-card clickable"
                      onClick={() => navigate(`/admin/products/${product.id}`)}
                    >
                      <img src={product.image || PRODUCT_PLACEHOLDER} alt={product.name} />
                      <div className="asd-product-info">
                        <h4>{product.name}</h4>
                        <p className="asd-product-price">{formatPriceRange(product.priceMin, product.priceMax)} / {product.unit}</p>
                        <p className="asd-product-moq">MOQ: {product.moq}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Company Snapshots */}
            {snapshots.length > 0 && (
              <div className="asd-section">
                <h3>Company Snapshot</h3>
                <div className="asd-snapshots-grid">
                  {snapshots.map((snapshot, index) => (
                    <img key={index} src={snapshot} alt={`Snapshot ${index + 1}`} className="asd-snapshot-image" />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="asd-tab-content">
            {/* Basic Information */}
            <div className="asd-section">
              <h3>Basic Information</h3>
              <div className="asd-info-grid">
                <div className="asd-info-item">
                  <label>Establish Year</label>
                  <span>{supplier.establishYear || 'N/A'}</span>
                </div>
                <div className="asd-info-item">
                  <label>License Number</label>
                  <span>{supplier.licenseNumber || 'N/A'}</span>
                </div>
                <div className="asd-info-item">
                  <label>Service Area</label>
                  <span>{supplier.serviceArea || supplier.country || 'N/A'}</span>
                </div>
                <div className="asd-info-item">
                  <label>Number of Employees</label>
                  <span>{supplier.numberOfEmployees || 'N/A'}</span>
                </div>
                <div className="asd-info-item">
                  <label>Category</label>
                  <span>{supplier.category || 'Construction'}</span>
                </div>
                <div className="asd-info-item">
                  <label>Annual Sales</label>
                  <span>{supplier.annualSales || 'N/A'}</span>
                </div>
                <div className="asd-info-item">
                  <label>Total Capitalization</label>
                  <span>{supplier.totalCapitalization || 'N/A'}</span>
                </div>
                <div className="asd-info-item">
                  <label>Website</label>
                  <span className="asd-link">{supplier.websiteLink || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="asd-section">
              <h3>Contact Information</h3>
              <div className="asd-info-grid">
                <div className="asd-info-item">
                  <label>Telephone</label>
                  <span>{supplier.phoneNumber || supplier.phone || 'N/A'}</span>
                </div>
                <div className="asd-info-item">
                  <label>Email</label>
                  <span>{supplier.email || 'N/A'}</span>
                </div>
                <div className="asd-info-item">
                  <label>Address</label>
                  <span>{supplier.address || 'N/A'}</span>
                </div>
                <div className="asd-info-item">
                  <label>Country</label>
                  <span>{supplier.country || 'N/A'}</span>
                </div>
                <div className="asd-info-item">
                  <label>City</label>
                  <span>{supplier.city || 'N/A'}</span>
                </div>
                <div className="asd-info-item">
                  <label>Zip Code</label>
                  <span>{supplier.zipCode || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Company Overview */}
            <div className="asd-section">
              <h3>Company Overview</h3>
              <p className="asd-text-content">{supplier.companyOverview || supplier.description || 'No company overview available.'}</p>
            </div>

            {/* Company Notes */}
            <div className="asd-section">
              <h3>Company Notes</h3>
              <p className="asd-text-content">{typeof supplier.companyNotes === 'string' ? supplier.companyNotes : 'No additional notes.'}</p>
            </div>

            {/* Brochure */}
            <div className="asd-section">
              <h3>Brochure</h3>
              <div className="asd-brochure">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <rect width="48" height="48" rx="8" fill="#F5F5F5"/>
                  <path d="M28 14H18C16.9 14 16 14.9 16 16V32C16 33.1 16.9 34 18 34H30C31.1 34 32 33.1 32 32V18L28 14Z" stroke="#CC4B4C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M28 14V18H32" stroke="#CC4B4C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20 22H28" stroke="#CC4B4C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20 26H28" stroke="#CC4B4C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <button 
                  className="asd-download-btn" 
                  onClick={handleDownloadBrochure}
                  disabled={!parseJSON(supplier.brochures).length}
                >
                  {parseJSON(supplier.brochures).length > 0 ? 'Download PDF' : 'No Brochure'}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'product' && (
          <div className="asd-tab-content">
            <div className="asd-section">
              <h3>All Products ({products.length})</h3>
              {products.length > 0 ? (
                <div className="asd-products-grid full">
                  {products.map((product) => (
                    <div key={product.id} className="asd-product-card">
                      <img src={product.image || PRODUCT_PLACEHOLDER} alt={product.name} />
                      <div className="asd-product-info">
                        <h4>{product.name}</h4>
                        <p className="asd-product-price">{formatPriceRange(product.priceMin, product.priceMax)} / {product.unit}</p>
                        <p className="asd-product-moq">MOQ: {product.moq}</p>
                        <p className="asd-product-category">{product.category?.name || 'N/A'}</p>
                        <button 
                          className="asd-view-product-btn"
                          onClick={() => navigate(`/admin/products/${product.id}`)}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="asd-no-products">
                  <p>No products available</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="asd-tab-content">
            <div className="asd-section">
              <h3>Contact Information</h3>
              <div className="asd-contact-card">
                <div className="asd-contact-items">
                  {(supplier.phoneNumber || supplier.phone) && (
                    <div className="asd-contact-item">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="#0397DD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{supplier.phoneNumber || supplier.phone}</span>
                    </div>
                  )}
                  {supplier.email && (
                    <div className="asd-contact-item">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#0397DD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="22,6 12,13 2,6" stroke="#0397DD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{supplier.email}</span>
                    </div>
                  )}
                  {supplier.address && (
                    <div className="asd-contact-item">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="#0397DD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="10" r="3" stroke="#0397DD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{supplier.address}, {supplier.city}, {supplier.country}</span>
                    </div>
                  )}
                  {supplier.websiteLink && (
                    <div className="asd-contact-item">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="#0397DD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <line x1="2" y1="12" x2="22" y2="12" stroke="#0397DD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="#0397DD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <a href={supplier.websiteLink} target="_blank" rel="noopener noreferrer">{supplier.websiteLink}</a>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Inquiry Form */}
            <div className="asd-section">
              <h3>Send Inquiry</h3>
              <div className="asd-inquiry-form">
                <div className="asd-form-row">
                  <div className="asd-form-group">
                    <label>Your Name <span className="required">*</span></label>
                    <input 
                      type="text" 
                      name="name"
                      value={inquiryForm.name}
                      onChange={handleInquiryChange}
                      placeholder="Enter your name" 
                    />
                  </div>
                  <div className="asd-form-group">
                    <label>Your Email <span className="required">*</span></label>
                    <input 
                      type="email" 
                      name="email"
                      value={inquiryForm.email}
                      onChange={handleInquiryChange}
                      placeholder="Enter your email" 
                    />
                  </div>
                </div>
                <div className="asd-form-row">
                  <div className="asd-form-group">
                    <label>Phone Number</label>
                    <input 
                      type="tel" 
                      name="phone"
                      value={inquiryForm.phone}
                      onChange={handleInquiryChange}
                      placeholder="Enter your phone number" 
                    />
                  </div>
                  <div className="asd-form-group">
                    <label>Company Name</label>
                    <input 
                      type="text" 
                      name="company"
                      value={inquiryForm.company}
                      onChange={handleInquiryChange}
                      placeholder="Enter your company name" 
                    />
                  </div>
                </div>
                <div className="asd-form-group full-width">
                  <label>Subject <span className="required">*</span></label>
                  <input 
                    type="text" 
                    name="subject"
                    value={inquiryForm.subject}
                    onChange={handleInquiryChange}
                    placeholder="Inquiry about products/services" 
                  />
                </div>
                <div className="asd-form-group full-width">
                  <label>Message <span className="required">*</span></label>
                  <textarea 
                    rows="5" 
                    name="message"
                    value={inquiryForm.message}
                    onChange={handleInquiryChange}
                    placeholder="Write your inquiry message here..."
                  ></textarea>
                </div>
                <button 
                  className="asd-btn-primary asd-submit-inquiry"
                  onClick={handleInquirySubmit}
                  disabled={inquiryLoading}
                >
                  {inquiryLoading ? 'Sending...' : 'Send Inquiry'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="asd-actions">
          <button className="asd-cancel-btn" onClick={() => navigate('/admin/suppliers')}>Cancel</button>
          {supplier.status === 'suspended' ? (
            <button 
              className="asd-approve-btn" 
              onClick={handleUnsuspend}
            >
              Unsuspend
            </button>
          ) : (supplier.status === 'approved' || supplier.status === 'published') ? (
            <button 
              className="asd-cancel-btn" 
              style={{ backgroundColor: '#ef4444', color: '#fff', borderColor: '#ef4444' }}
              onClick={handleSuspend}
            >
              Suspend
            </button>
          ) : (
            <>
              <button 
                className="asd-approve-btn" 
                onClick={() => handleStatusChange('approved')}
              >
                Approve
              </button>
              <button 
                className="asd-cancel-btn" 
                style={{ backgroundColor: '#ef4444', color: '#fff', borderColor: '#ef4444' }}
                onClick={handleSuspend}
              >
                Suspend
              </button>
            </>
          )}
        </div>

        {/* Supplier Details Modal */}
        {showModal && (
          <SupplierManageModal 
            supplier={supplier}
            onClose={() => setShowModal(false)}
            onStatusChange={handleStatusChange}
          />
        )}

        {/* Supplier Edit Modal */}
        <SupplierEditModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          supplier={supplier}
          onUpdate={handleEditUpdate}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminSupplierDetailPage;
